// ─────────────────────────────────────────────────────────────────────────────
// DpaPage, the data processing addendum at /dpa.
//
// This is the document a customer sends to their reviewer, so it is laid out to
// be read end to end rather than skimmed: a sticky index on the left that tracks
// the clause you are in, one column of prose on the right at a comfortable
// measure, numbered clauses, and hairlines between them. No cards wrap the
// running prose, and there is no decoration competing with it.
//
// Two things a reviewer always asks for are given as tables rather than
// sentences: the annex describing the processing, and the sub-processor list.
// That list is the real one. There are five entries, one of which is the store
// the customer connects themselves, and it deliberately does not include an
// accounting connection or a bank, because neither exists: QuickBooks data
// leaves as a file the customer imports, and bank data only ever arrives as a
// statement the customer uploads.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    NUMS,
    Icon,
    Container,
    Section,
    Heading,
    CardTitle,
    Lead,
    Pill,
    SectionHead,
    PageHero,
    CTASection,
    MarketingShell,
    Seo,
    BTN_SECONDARY,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

const LAST_UPDATED = 'September 2, 2026';

// ── Annex I, the description of the processing ───────────────────────────────
const ANNEX = [
    {
        label: 'Subject matter',
        value: 'Provision of the Wavelength back office: daily close, cash control, reconciliation, reporting, inventory, production, wholesale invoicing, and the companion apps that feed them.',
    },
    {
        label: 'Nature and purpose',
        value: 'Collecting, recording, organising, storing, matching, retrieving and exporting the records the Customer connects or enters, so that the Customer can close each trading day and reconcile it.',
    },
    {
        label: 'Categories of data subjects',
        value: 'Staff and authorised users of the Customer, and the customers of the Customer whose orders, deliveries and payments pass through the service.',
    },
    {
        label: 'Categories of personal data',
        value: 'Names, work email addresses, roles, locations and sign-in identifiers; order and payment metadata such as tender type, card brand, last four digits, amount and settlement batch; delivery names, addresses and contact details; usage, device and audit records.',
    },
    {
        label: 'Special category data',
        value: 'None is requested by the service or required to use it. The Customer should not place special category data in free-text fields.',
    },
    {
        label: 'Frequency',
        value: 'Continuous for the duration of the agreement, as records arrive from the dashboard, the companion apps, the connected storefront and the files the Customer uploads.',
    },
    {
        label: 'Duration',
        value: 'The term of the underlying agreement, plus the limited period after it during which data is made available for export before deletion.',
    },
];

// ── The sub-processor list ───────────────────────────────────────────────────
const SUBPROCESSORS = [
    {
        name: 'Amazon Web Services',
        purpose: 'Hosting, database, file storage and authentication. The service runs on it.',
        region: 'United States',
    },
    {
        name: 'Google',
        purpose: 'Analytics for the traffic and campaign figures on the store the Customer connects, and Maps for the delivery map.',
        region: 'United States',
    },
    {
        name: 'Datacap',
        purpose: 'Card transaction and settlement records, where the Customer uses Datacap as its processor.',
        region: 'United States',
    },
    {
        name: 'OpenAI',
        purpose: 'The in-app assistant. A question and the slice of workspace data needed to answer it are sent only when somebody asks one.',
        region: 'United States',
    },
    {
        name: 'The connected WordPress storefront',
        purpose: 'Orders and products read from the store the Customer runs. This is the system of the Customer rather than one we chose, and the data flows towards us.',
        region: 'Wherever the Customer hosts it',
    },
];

// ── Clauses ──────────────────────────────────────────────────────────────────
const CLAUSES = [
    {
        id: 'dpa-roles',
        nav: 'Roles of the parties',
        title: 'Roles of the parties',
        body: [
            'For the personal data processed through the service, the Customer is the controller and Wavelength is the processor. The Customer decides the purposes and the means of the processing. Wavelength processes personal data only on the documented instructions of the Customer, which include the underlying agreement, this addendum, and the configuration choices the Customer makes inside the product.',
            'If in our opinion an instruction would break applicable data protection law, we will say so rather than carry it out quietly.',
        ],
    },
    {
        id: 'dpa-scope',
        nav: 'Scope of processing',
        title: 'Scope of the processing',
        body: [
            'Wavelength processes personal data as needed to provide the service: taking in orders, tenders, processor batches, deposits and counts; matching them against each other; producing sales, inventory, production and wholesale reporting; running the companion apps on the floor; and administering accounts and support.',
            'The annex below describes the processing in the form a review usually asks for.',
        ],
        annex: true,
    },
    {
        id: 'dpa-instructions',
        nav: 'Customer instructions',
        title: 'Customer instructions',
        body: [
            'Wavelength will not sell personal data, will not use it for advertising, and will not use it to build or train its own models. We use it to run the service for the Customer, to support the Customer, to keep the platform secure, and to meet our own legal obligations, and for nothing else.',
            'Where the law requires us to process personal data beyond the instructions of the Customer, we will tell the Customer before we do so, unless that law forbids the notice.',
        ],
    },
    {
        id: 'dpa-confidentiality',
        nav: 'Confidentiality',
        title: 'Confidentiality',
        body: [
            'Everybody at Wavelength who is authorised to process personal data is bound by an obligation of confidentiality and gets access only to what their work requires. Access is reviewed when a role changes and removed when somebody leaves.',
        ],
    },
    {
        id: 'dpa-security',
        nav: 'Security measures',
        title: 'Security measures',
        body: [
            'Wavelength maintains technical and organisational measures appropriate to the risk, including:',
            {
                list: [
                    'Encryption of personal data in transit and at rest.',
                    'Role-based access control across every module of the product, applied per person and narrowed to the locations that person runs.',
                    'Workspace isolation, so every read and every write is scoped to one workspace even when one person can reach several.',
                    'Single-use staff sign-in codes that expire, and session tokens refreshed rather than shared.',
                    'A permanent audit log of manual changes, recording who changed what and when.',
                    'An append-only inventory ledger and credited rather than deleted invoices, so a correction is a new entry instead of an overwrite.',
                    'Dashboard corrections written to their own layer, leaving the imported source record with the values it arrived with.',
                    'Secrets and credentials held on the server and never shipped to the browser.',
                ],
            },
            <>
                We may change these measures as the platform develops, provided the overall level of protection is not reduced. The{' '}
                <DocLink to="/security">security page</DocLink> keeps a current list, including the certifications and reports we do not yet hold.
            </>,
        ],
    },
    {
        id: 'dpa-subprocessors',
        nav: 'Sub-processors',
        title: 'Sub-processors',
        body: [
            'The Customer gives a general authorisation for Wavelength to engage the sub-processors listed below. Each is bound by data protection obligations no less protective than this addendum, and Wavelength remains responsible for what they do with personal data.',
        ],
        table: true,
        after: [
            'We will give reasonable notice before adding a sub-processor, and the Customer may object on reasonable data protection grounds. If we cannot resolve the objection, the Customer may stop using the feature that depends on the new sub-processor or terminate the affected part of the agreement.',
            'Two connections a reviewer often expects are absent by design. There is no accounting integration: the QuickBooks export is a journal entry file the Customer downloads and imports, so no accounting provider receives anything from us. There is no bank connection: bank data reaches the service only in a statement file the Customer uploads.',
        ],
    },
    {
        id: 'dpa-rights',
        nav: 'Data subject rights',
        title: 'Assistance with data subject rights',
        body: [
            'Taking into account the nature of the processing, Wavelength will help the Customer respond to requests from data subjects exercising their rights of access, correction, deletion, restriction, objection and portability, using the tools in the product and reasonable further assistance where those are not enough.',
            'If a data subject comes to us directly, we will pass the request to the Customer rather than answer on their behalf, unless the law requires otherwise. We will also give reasonable help with data protection impact assessments and with consultations with a supervisory authority.',
        ],
    },
    {
        id: 'dpa-breach',
        nav: 'Breach notification',
        title: 'Personal data breach notification',
        body: [
            'Wavelength will notify the Customer without undue delay after becoming aware of a personal data breach affecting the personal data of that Customer. The notice will describe, as far as we know it at the time, what happened, which categories and roughly how many records are involved, the likely consequences, and what we are doing about it, so the Customer can meet its own notification duties.',
            'We will keep the Customer updated as more is known rather than waiting until we have the complete picture.',
        ],
    },
    {
        id: 'dpa-deletion',
        nav: 'Return and deletion',
        title: 'Return and deletion',
        body: [
            'On termination or expiry, and at the choice of the Customer, Wavelength will return or delete the personal data it processes on behalf of the Customer and delete existing copies, unless the law requires us to keep them. Data can be exported from every table as CSV, Excel or PDF before deletion, and we make it available for a limited period after the account ends so that export is possible.',
            'Backups age out on their own schedule. Where a record survives in a backup after deletion, it stays isolated and is deleted as that backup expires.',
        ],
    },
    {
        id: 'dpa-transfers',
        nav: 'International transfers',
        title: 'International transfers',
        body: [
            'The service is operated from the United States and runs on infrastructure there, as do the sub-processors listed above. Where personal data is transferred out of the region it was collected in, Wavelength will put in place a transfer mechanism recognised under the applicable data protection law, such as standard contractual clauses, together with any supplementary measures reasonably required.',
        ],
    },
    {
        id: 'dpa-audits',
        nav: 'Audits and information',
        title: 'Audits and information',
        body: [
            'Wavelength will make available to the Customer the information reasonably needed to show compliance with this addendum, and will allow for and contribute to audits by the Customer or an independent auditor appointed by the Customer. Audits happen on reasonable notice, no more than once a year unless a supervisory authority or a breach requires otherwise, under confidentiality, and in a way that protects the security and the data of other customers.',
            'In practice most reviews are answered with a walkthrough of the architecture, the access model and the audit log, and we would rather do that than exchange documents.',
        ],
    },
    {
        id: 'dpa-agreement',
        nav: 'Relationship to the agreement',
        title: 'Relationship to the agreement',
        body: [
            <>
                This addendum forms part of the agreement between the Customer and Wavelength for use of the service. Where it conflicts with the{' '}
                <DocLink to="/terms#terms">terms of service</DocLink> on the processing of personal data, this addendum wins. The liability limits in the underlying agreement apply to this addendum as well, taken together rather than separately.
            </>,
        ],
    },
    {
        id: 'dpa-law',
        nav: 'Governing law',
        title: 'Governing law',
        body: [
            'This addendum is governed by the same law and the same dispute resolution and jurisdiction provisions as the underlying agreement, except where mandatory data protection law requires otherwise.',
        ],
    },
    {
        id: 'dpa-contact',
        nav: 'Contact',
        title: 'Contact',
        body: [
            <>
                Questions about this addendum, requests for a signed copy, and anything a review needs go to <MailLink address="privacy@wavelengtherp.com" />. A person answers them.
            </>,
        ],
    },
];

// ── Local prose primitives ───────────────────────────────────────────────────
// The kit has no document type, so these are defined here: 15px on a 620px
// measure, which is the width a long clause stays readable at. Nothing is boxed.

function DocLink({ to, children }) {
    return (
        <Link to={to} className="font-medium text-[#0A0A0A] underline decoration-[rgba(10,10,10,0.30)] underline-offset-[3px] transition-colors hover:decoration-[#0A0A0A]">
            {children}
        </Link>
    );
}

function MailLink({ address }) {
    return (
        <a href={`mailto:${address}`} className="font-medium text-[#0A0A0A] underline decoration-[rgba(10,10,10,0.30)] underline-offset-[3px] transition-colors hover:decoration-[#0A0A0A]">
            {address}
        </a>
    );
}

function P({ children }) {
    return <p className="mt-4 text-[15px] leading-[1.75] text-[#6B6B6B]">{children}</p>;
}

function Bullets({ items }) {
    return (
        <ul className="mt-4 flex flex-col gap-2.5">
            {items.map((it, i) => (
                <li key={i} className="flex gap-3 text-[15px] leading-[1.75] text-[#6B6B6B]">
                    <span className="mt-[13px] h-px w-3 flex-none bg-[rgba(10,10,10,0.25)]" />
                    <span>{it}</span>
                </li>
            ))}
        </ul>
    );
}

function Prose({ items }) {
    return items.map((item, i) => (
        item && item.list ? <Bullets key={i} items={item.list} /> : <P key={i}>{item}</P>
    ));
}

// Annex I: label on the left, description on the right, hairline between rows.
function AnnexTable({ rows }) {
    return (
        <div className="mt-6 overflow-hidden rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white">
            {rows.map((r, i) => (
                <div key={r.label} className={`grid grid-cols-1 gap-1 px-5 py-4 sm:grid-cols-[168px_1fr] sm:gap-6 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)]' : ''}`}>
                    <div className="text-[13px] font-medium text-[#0A0A0A]">{r.label}</div>
                    <div className="text-[14px] leading-[1.65] text-[#6B6B6B]">{r.value}</div>
                </div>
            ))}
        </div>
    );
}

// The sub-processor list. Three columns on a wide screen, stacked on a phone,
// scrolling inside itself rather than pushing the page sideways.
function SubprocessorTable({ rows }) {
    return (
        <div className="mt-6 overflow-hidden rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white">
            <div className="hidden grid-cols-[180px_1fr_140px] gap-6 border-b border-[rgba(10,10,10,0.10)] bg-[#FAFAFA] px-5 py-3 text-[12px] font-medium uppercase tracking-[0.12em] text-[#A1A1A1] sm:grid">
                <div>Provider</div>
                <div>What it does</div>
                <div>Where</div>
            </div>
            {rows.map((r, i) => (
                <div key={r.name} className={`grid grid-cols-1 gap-1.5 px-5 py-4 sm:grid-cols-[180px_1fr_140px] sm:gap-6 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)]' : ''}`}>
                    <div className="text-[14px] font-medium text-[#0A0A0A]">{r.name}</div>
                    <div className="text-[14px] leading-[1.65] text-[#6B6B6B]">{r.purpose}</div>
                    <div className="text-[13px] leading-[1.65] text-[#A1A1A1]">{r.region}</div>
                </div>
            ))}
        </div>
    );
}

// ── The index rail ───────────────────────────────────────────────────────────
function useActiveId(ids) {
    const [active, setActive] = useState(ids[0]);
    useEffect(() => {
        if (typeof IntersectionObserver !== 'function') return undefined;
        const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
        if (!els.length) return undefined;
        const visible = new Map();
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => visible.set(e.target.id, e.isIntersecting));
                const first = ids.find((id) => visible.get(id));
                if (first) setActive(first);
            },
            { rootMargin: '-104px 0px -68% 0px', threshold: 0 },
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [ids]);
    return active;
}

function TocLink({ id, active, children }) {
    return (
        <a
            href={`#${id}`}
            className={`block rounded-full px-3 py-1.5 text-[13px] transition-colors ${active ? 'bg-[#F5F5F5] font-medium text-[#0A0A0A]' : 'text-[#A1A1A1] hover:text-[#0A0A0A]'}`}
        >
            {children}
        </a>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function DpaPage() {
    const ids = useMemo(() => ['dpa', ...CLAUSES.map((c) => c.id)], []);
    const active = useActiveId(ids);

    return (
        <MarketingShell>
            <Seo
                title="Data processing addendum"
                description="The Wavelength data processing addendum: controller and processor roles, the sub-processor list, security measures, breach notice, transfers, audits, deletion."
                path="/dpa"
                keywords="data processing addendum, DPA, GDPR processor terms, sub-processor list, standard contractual clauses, personal data breach notification, retail software data protection"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'WebPage',
                            '@id': 'https://wavelengtherp.com/dpa#webpage',
                            url: 'https://wavelengtherp.com/dpa',
                            name: 'Data processing addendum',
                            description: 'How Wavelength processes personal data on behalf of a customer: roles, instructions, security measures, sub-processors, breach notice, transfers, audits, return and deletion.',
                            dateModified: '2026-09-02',
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'Legal', item: 'https://wavelengtherp.com/privacy' },
                                { '@type': 'ListItem', position: 3, name: 'Data processing addendum', item: 'https://wavelengtherp.com/dpa' },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="Legal"
                title="Data processing addendum."
                lead="The terms on which Wavelength processes personal data on your behalf. It is written to be sent straight to whoever reviews it, with the two things they always ask for, the description of the processing and the sub-processor list, set out as tables rather than buried in a sentence."
            >
                <div className="flex flex-col gap-5 border-t border-[rgba(10,10,10,0.10)] pt-6">
                    <div className="text-[13px] text-[#A1A1A1]">Last updated {LAST_UPDATED}. Written to sit alongside the terms of service.</div>
                    <div className="flex flex-wrap gap-2">
                        <Pill as="a" href="#dpa-subprocessors" active>Five sub-processors</Pill>
                        <Pill as={Link} to="/privacy#privacy">Privacy notice</Pill>
                        <Pill as={Link} to="/terms#terms">Terms of service</Pill>
                        <Pill as={Link} to="/security">Security</Pill>
                    </div>
                </div>
            </PageHero>

            {/* ── Index rail plus the document ───────────────────────────── */}
            <Section pad="md">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[200px_1fr] lg:gap-16">
                        <nav aria-label="On this page" className="hidden lg:block">
                            {/* The index can run past a short viewport, so the rail
                                scrolls inside itself instead of clipping. */}
                            <div className="sticky top-24 flex max-h-[calc(100vh-7rem)] flex-col overflow-y-auto pb-4 [scrollbar-width:thin]">
                                <div className="px-3 text-[12px] font-medium uppercase tracking-[0.14em] text-[#A1A1A1]">On this page</div>
                                <div className="mt-4 flex flex-col gap-0.5">
                                    <TocLink id="dpa" active={active === 'dpa'}>Addendum</TocLink>
                                    {CLAUSES.map((c) => (
                                        <TocLink key={c.id} id={c.id} active={active === c.id}>{c.nav}</TocLink>
                                    ))}
                                </div>
                                <div className="mt-4 border-t border-[rgba(10,10,10,0.08)] px-3 pt-4 flex flex-col gap-2">
                                    <Link to="/privacy#privacy" className="text-[13px] text-[#A1A1A1] transition-colors hover:text-[#0A0A0A]">Privacy notice</Link>
                                    <Link to="/terms#terms" className="text-[13px] text-[#A1A1A1] transition-colors hover:text-[#0A0A0A]">Terms of service</Link>
                                </div>
                            </div>
                        </nav>

                        <div className="min-w-0 max-w-[620px]">
                            <div id="dpa" className="scroll-mt-28">
                                <Heading>The addendum.</Heading>
                            </div>
                            <Lead className="mt-5">
                                This addendum forms part of the agreement between you, the Customer, and Wavelength ERP for your use of the service, and sets out how we process personal data on your behalf. Where it conflicts with the underlying agreement on the processing of personal data, this addendum controls.
                            </Lead>

                            {CLAUSES.map((c, n) => (
                                <section
                                    key={c.id}
                                    id={c.id}
                                    className="mt-10 scroll-mt-28 border-t border-[rgba(10,10,10,0.08)] pt-10"
                                >
                                    <div className={`text-[13px] font-medium text-[#A1A1A1] ${NUMS}`}>
                                        {String(n + 1).padStart(2, '0')}
                                    </div>
                                    <CardTitle className="mt-2">{c.title}</CardTitle>
                                    <Prose items={c.body} />
                                    {c.annex && <AnnexTable rows={ANNEX} />}
                                    {c.table && <SubprocessorTable rows={SUBPROCESSORS} />}
                                    {c.after && <Prose items={c.after} />}
                                </section>
                            ))}
                        </div>
                    </div>
                </Container>
            </Section>

            {/* ── What a reviewer usually asks next ──────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[340px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4">
                                <SectionHead
                                    title="The rest of the review."
                                    lead="This addendum answers the processing questions. The two pages next to it answer the ones that come after, including the certifications we do not hold."
                                    maxWidth="max-w-[420px]"
                                />
                                <div className="mt-2">
                                    <Link to="/contact" className={BTN_SECONDARY}>
                                        Send us a questionnaire
                                        <Icon name="arrow" className="h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {[
                                    {
                                        to: '/security',
                                        title: 'Security and access control',
                                        body: 'Where the software runs, what cannot be overwritten, who can see what, and an honest list of what has not been done yet.',
                                    },
                                    {
                                        to: '/privacy#privacy',
                                        title: 'Privacy notice',
                                        body: 'What we collect, where it comes from, who else touches it, how long it stays and the rights a person has over it.',
                                    },
                                    {
                                        to: '/terms#terms',
                                        title: 'Terms of service',
                                        body: 'What the service is, who owns the data in it, how it is billed, and what happens when an account ends.',
                                    },
                                    {
                                        to: '/privacy#cookies',
                                        title: 'Cookie notice',
                                        body: 'The short list of cookies and browser storage the site and the dashboard set, and what each one is for.',
                                    },
                                ].map((c) => (
                                    <Link
                                        key={c.to}
                                        to={c.to}
                                        className="group flex flex-col gap-2 rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white p-6 transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-[rgba(10,10,10,0.30)]"
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-[16px] font-semibold tracking-[-0.01em] text-[#0A0A0A]">{c.title}</span>
                                            <Icon name="arrow" className="h-4 w-4 flex-none text-[#A1A1A1] transition-colors group-hover:text-[#0A0A0A]" />
                                        </div>
                                        <p className="text-[14px] leading-[1.6] text-[#6B6B6B]">{c.body}</p>
                                    </Link>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Legal"
                title="Send it to your reviewer."
                body="If your review needs a signed copy, a filled questionnaire or a walkthrough of the access model on a live workspace, ask and you will get one from somebody who built it."
                primary={{ label: 'Talk to us', to: '/contact' }}
                secondary={{ label: 'How it is built', to: '/security' }}
            />
        </MarketingShell>
    );
}
